import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { invoiceListQuery } from '@xdocs/schemas';
import type { ErrorAlert, Invoice, InvoiceListResult } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'issuedAt', dir: 'desc' };
export const ADMIN_INVOICE_LIST_QUERY_KEY_PREFIX = 'admin-invoice-list' as const;

export interface UseAdminInvoicesListResult {
  rows: readonly Invoice[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/**
 * `billing.invoices.list` (F26) reused for the admin drill-down (F70 AC:
 * "reuse `invoiceListQuery`... rather than a duplicate op",
 * `packages/schemas/src/billing.dto.ts`) — the SAME op the user-facing
 * `/settings/billing` invoices tab calls; this assumes the server scopes the
 * result to ALL invoices for an admin-role caller vs. the caller's own for
 * a regular user, since the query schema itself carries no `userId` filter
 * (see returned open-issues list — this is the only plausible frozen-
 * contract path, not independently confirmed against a server handler that
 * doesn't exist yet).
 */
export function useAdminInvoicesList(): UseAdminInvoicesListResult {
  const { wsClient, connectionState } = useWs();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<Invoice[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = [ADMIN_INVOICE_LIST_QUERY_KEY_PREFIX, query] as const;

  const result = useQuery<InvoiceListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'billing.invoices.list', invoiceListQuery.parse(query)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
  };
}
